import { difference, without } from 'lodash';
import { APIComponentAttributes, ArdoqId } from '@ardoq/api-types';
import { isTruthy } from '@ardoq/common-helpers';
import { ContextShape } from '@ardoq/data-model';
import { dispatchAction } from '@ardoq/rxbeach';

import {
  context as Context,
  CloseWorkspaceOpts,
  SetOpenWorkspacesArgs,
} from 'context';
import { logError } from '@ardoq/logging';
import { CurrentContext, TrackingLocation } from '@ardoq/data-model';
import { referenceInterface } from '@ardoq/reference-interface';
import { workspaceInterface } from '@ardoq/workspace-interface';
import { componentInterface } from '@ardoq/component-interface';
import { setSort as setSortAction } from 'streams/context/ContextActions';
import { trackOpenedWorkspace } from 'tracking/events/core';
import {
  getConnectedWorkspaceIds as getConnectedWorkspaceIdsFromMap,
  linkedWorkspaces$,
} from 'streams/linkedWorkspaces$';

// NOTE:
// The purpose of BB interfaces is to avoid exposing the BB model. In practice
// this means you should __never__ return a BB model from these functions.

const trackNewlyOpenedWorkspaces = (
  beforeIds: ArdoqId[],
  afterIds: ArdoqId[],
  trackingLocation: TrackingLocation
): void => {
  const newlyOpenedIds = difference(afterIds, beforeIds);
  newlyOpenedIds.forEach(workspaceId => {
    const workspace = workspaceInterface.get(workspaceId);
    if (workspace) {
      trackOpenedWorkspace({
        resourceId: workspaceId,
        useCase: workspace['ardoq-persistent']?.['use-case'],
        from: trackingLocation,
      });
    }
  });
};

const getActiveWorkspaceId = (): ArdoqId | null => {
  return Context.activeWorkspaceId();
};

const getConnectedWorkspaceIds = (): Set<string> => {
  return getConnectedWorkspaceIdsFromMap(
    Context.workspaceIds(),
    linkedWorkspaces$.state
  );
};

const getCurrentComponentId = (): ArdoqId | null => {
  return Context.componentId();
};

const getReferenceId = (): ArdoqId | null => {
  return Context.referenceId();
};

const getLoadedWorkspaceIds = (): ArdoqId[] => Context.workspaceIds();

const getLoadedModelIds = (): ArdoqId[] =>
  Context.workspaceIds()
    .map(workspaceId => workspaceInterface.get(workspaceId))
    .filter(isTruthy)
    .map(workspace => workspace.componentModel);

const setComponentById = async (
  componentId: string,
  trackingLocation?: TrackingLocation
): Promise<void> => {
  const component = componentInterface.get(componentId);
  if (!component) {
    Context.unsetComponent();
    return;
  }

  if (Context.componentId() === componentId && Context.referenceId() === null) {
    return;
  }

  if (component.rootWorkspace === Context.activeWorkspaceId()) {
    Context.setComponentUnsafe(componentId);
    return;
  }

  const workspaceId = component.rootWorkspace;
  const workspace = workspaceInterface.get(workspaceId);

  if (!workspace) {
    return;
  }

  const beforeIds = Context.workspaceIds();

  await Context.loadWorkspaces({
    contextWorkspace: workspace,
    resetComponentContext: false,
    workspaces: [],
  });

  if (trackingLocation) {
    trackNewlyOpenedWorkspaces(
      beforeIds,
      Context.workspaceIds(),
      trackingLocation
    );
  }

  Context.setComponentUnsafe(componentId);
};

const setReferenceById = (
  referenceId: string,
  skipEvent?: boolean | undefined
): void => {
  const referenceData = referenceInterface.get(referenceId);
  if (!referenceData) {
    Context.unsetReference(skipEvent);
    return;
  }
  return Context.setReference(referenceData, skipEvent);
};

const isExploreMode = (): boolean => {
  return Context.isExploreMode();
};

/**
 * Closing a workspace means removing it from the workspaces in Context and
 * clearing components, references, tags, and linked/connected workspaces that
 * are no longer in use, as well as closing web-socket listeners on that
 * workspace.
 */
const closeWorkspace = (workspaceId: ArdoqId, options: CloseWorkspaceOpts) => {
  const foundWorkspaceId = Context.workspaceIds().find(
    id => id === workspaceId
  );
  if (!foundWorkspaceId) {
    return { error: 'Workspace does not exist in context' };
  }

  Context.closeWorkspace(workspaceId, options);
  return { error: null };
};

/**
 * See closeWorkspace() for docs.
 */
const closeWorkspaces = (
  workspaceIds: ArdoqId[],
  options: CloseWorkspaceOpts
) => {
  workspaceIds.forEach(workspaceId => {
    closeWorkspace(workspaceId, options);
  });
};

const switchToWorkspace = async (
  workspaceId: ArdoqId,
  trackingLocation?: TrackingLocation
): Promise<void> => {
  const workspace = workspaceInterface.get(workspaceId);
  if (!workspace) {
    return Promise.resolve();
  }
  const beforeIds = Context.workspaceIds();

  await Context.switchToWorkspace(workspace);

  if (trackingLocation) {
    trackNewlyOpenedWorkspaces(
      beforeIds,
      Context.workspaceIds(),
      trackingLocation
    );
  }
};

const loadWorkspacesInBackground = async (
  workspaceIds: ArdoqId[],
  trackingLocation?: TrackingLocation
): Promise<void> => {
  const workspaces = workspaceIds
    .map(workspaceId => workspaceInterface.get(workspaceId))
    .filter(isTruthy);

  const beforeIds = Context.workspaceIds();

  await Context.loadWorkspacesInBackground(workspaces);

  if (trackingLocation) {
    trackNewlyOpenedWorkspaces(
      beforeIds,
      Context.workspaceIds(),
      trackingLocation
    );
  }
};

const loadWorkspacesAndSetContext = async (
  contextWorkspaceId: ArdoqId,
  workspaceIds: ArdoqId[],
  trackingLocation?: TrackingLocation
): Promise<void> => {
  const contextWorkspace = workspaceInterface.get(contextWorkspaceId);
  const workspaces = workspaceIds
    .map(workspaceId => workspaceInterface.get(workspaceId))
    .filter(isTruthy);

  const beforeIds = Context.workspaceIds();

  if (!contextWorkspace) {
    await Context.loadWorkspacesInBackground(workspaces);
  } else {
    await Context.loadWorkspacesAndSetContext(contextWorkspace, workspaces);
  }

  if (trackingLocation) {
    trackNewlyOpenedWorkspaces(
      beforeIds,
      Context.workspaceIds(),
      trackingLocation
    );
  }
};

type SetOpenWorkspacesByIdArgs = Omit<
  SetOpenWorkspacesArgs,
  'workspaces' | 'activeWorkspace'
> & {
  workspaceIds: ArdoqId[];
  activeWorkspaceId: ArdoqId | null;
  trackingLocation?: TrackingLocation | null;
};

const setOpenWorkspaces = ({
  workspaceIds,
  activeWorkspaceId,
  trackingLocation,
  ...args
}: SetOpenWorkspacesByIdArgs) => {
  const beforeIds = Context.workspaceIds();

  Context.setOpenWorkspaces({
    ...args,
    workspaces: workspaceIds
      .map(workspaceId => workspaceInterface.get(workspaceId))
      .filter(isTruthy),
    activeWorkspace: activeWorkspaceId
      ? workspaceInterface.get(activeWorkspaceId)
      : undefined,
  });

  if (trackingLocation) {
    trackNewlyOpenedWorkspaces(
      beforeIds,
      Context.workspaceIds(),
      trackingLocation
    );
  }
};

const isContextWorkspace = (workspaceId: ArdoqId) => {
  return Context.activeWorkspaceId() === workspaceId;
};

const getSelectedComponent = (): APIComponentAttributes | undefined => {
  const componentId = Context.componentId();
  if (!componentId) return;
  return componentInterface.get(componentId) ?? undefined;
};

/**
 * Context.setX functions are mutually exclusive, so to set all attributes of
 * context we need to diff the incoming object with current context in order
 * to determine which functions to call.
 * It is assumed that the incoming context is consistent with the rules in
 * Context. For example: componentId AND referenceId cannot be set at the same
 * time.
 */
const setContext = async (incoming: ContextShape) => {
  const {
    componentId = '',
    referenceId = '',
    scenarioId = '',
    workspaceId = '',
    workspaceIds = [],
  } = incoming;
  const current = {
    componentId: Context.componentId() ?? '',
    referenceId: Context.referenceId() ?? '',
    scenarioId: Context.scenarioId() ?? '',
    workspaceId: Context.activeWorkspaceId() ?? '',
    workspaceIds: getLoadedWorkspaceIds(),
  };

  // Close workspaces that are no longer in context
  const closedWorkspaceIds = without(current.workspaceIds, ...workspaceIds);
  if (closedWorkspaceIds.length > 0) {
    closeWorkspaces(closedWorkspaceIds, { isRegisterLoadedState: false });
  }

  // Open workspaces that are new, and set contextWorkspace
  const openedWorkspaceIds = without(workspaceIds, ...current.workspaceIds);

  if (workspaceId !== current.workspaceId) {
    // Omit workspaceId because it will be loaded twice if passed as
    // contextWorkspaceId and within workspaceIds.
    await contextInterface.loadWorkspacesAndSetContext(
      workspaceId,
      without(openedWorkspaceIds, workspaceId)
    );
  } else if (openedWorkspaceIds.length > 0) {
    await contextInterface.loadWorkspacesInBackground(openedWorkspaceIds);
  }

  if (componentId && scenarioId) {
    logError(
      new Error('context.{componentId,scenarioId} set at the same time')
    );
  }

  // Clear context manually
  Context.unsetComponent();
  Context.unsetReference();
  Context.setScenarioId(null);

  // scenarioId and componentId are mutually exclusive, and since the incoming
  // context is assumed to be consistent with the rules in Context, we can
  // safely set the ids that exist.
  if (componentId) {
    setComponentById(componentId);
  }
  if (referenceId) {
    setReferenceById(referenceId);
  }
  if (scenarioId) {
    Context.setScenarioId(scenarioId);
  }
  dispatchAction(setSortAction(incoming.sort));
};

const getCurrentContext = (): CurrentContext => {
  const workspaceId = getActiveWorkspaceId();

  if (!workspaceId) {
    return undefined;
  }
  return {
    workspaceId,
    componentId: getCurrentComponentId() || undefined,
  };
};

/**
 * `contextInterface` is a Backbone-era singleton: the readers snapshot the
 * `Context` singleton at call time, and the writers mutate it imperatively.
 *
 * The app is stream-based now. Join `context$` (`streams/context/context$.ts`,
 * emitting `ContextShape`) at a permitted boundary -- a `*$.ts` file, a file
 * inside a `*$/` folder, or a `*routines.ts` -- and thread the resolved context
 * downstream as plain parameters. In routines, prefer putting the value on the
 * action payload at the dispatch site; `withLatestFrom(context$)` is the
 * fallback. The writers should be replaced by dispatching the matching action
 * from `streams/context/ContextActions.ts`.
 *
 * Every member below is deprecated. See
 * `packages/eslint-plugin-ardoq-lint/docs/rules/no-model-interface-call.md`
 * for the hoisting recipe and the interface-to-stream substitution table.
 */
export const contextInterface = {
  /**
   * @deprecated Join `contextComponentId$` (`streams/context/context$.ts`).
   */
  getCurrentComponentId,
  /**
   * @deprecated Dispatch the matching action from `streams/context/ContextActions.ts` and let the context routines do the work.
   */
  switchToWorkspace,
  /**
   * @deprecated Dispatch the matching action from `streams/context/ContextActions.ts` and let the context routines do the work.
   */
  loadWorkspacesAndSetContext,
  /**
   * @deprecated Dispatch the matching action from `streams/context/ContextActions.ts` and let the context routines do the work.
   */
  loadWorkspacesInBackground,
  /**
   * @deprecated Dispatch the matching action from `streams/context/ContextActions.ts` and let the context routines do the work.
   */
  closeWorkspaces,
  /**
   * @deprecated Join `contextWorkspaceId$` (`streams/context/context$.ts`).
   */
  getActiveWorkspaceId,
  /**
   * @deprecated Join `linkedWorkspaces$` (`streams/linkedWorkspaces$.ts`).
   */
  getConnectedWorkspaceIds,
  /**
   * @deprecated Join `context$` and `workspaces$` and map the open workspaces to `componentModel`; `entityOperations.toIds` (`@ardoq/core`) covers the id extraction.
   */
  getLoadedModelIds,
  /**
   * @deprecated Join `context$` and read `workspaceIds`.
   */
  getLoadedWorkspaceIds,
  /**
   * @deprecated Join `contextReferenceId$` (`streams/context/context$.ts`).
   */
  getReferenceId,
  /**
   * @deprecated Join `context$` and `components$` and read `components$.byId[componentId]`.
   */
  getSelectedComponent,
  /**
   * @deprecated Join `context$` and read `isExploreMode`.
   */
  isExploreMode,
  /**
   * @deprecated Join `contextWorkspaceId$` (`streams/context/context$.ts`) and compare.
   */
  isContextWorkspace,
  /**
   * @deprecated Dispatch the matching action from `streams/context/ContextActions.ts`.
   */
  setOpenWorkspaces,
  /**
   * @deprecated Dispatch the matching action from `streams/context/ContextActions.ts`.
   */
  setComponentById,
  /**
   * @deprecated Dispatch the matching action from `streams/context/ContextActions.ts`.
   */
  setContext,
  /**
   * @deprecated Join `context$` (`streams/context/context$.ts`).
   */
  getCurrentContext,
};
